/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import {
  DataActionConditionType,
  getTopLevelKeyFromConditionType,
  isTypeEditable,
} from 'cio/utils/campaigns/data-action';
import { findUsedVariablePaths } from 'cio/utils/liquid';
import { keyPathToString } from 'cio/utils/objects';
import { Select } from 'ember-power-select/components/power-select';
import CampaignModel from 'cio/models/campaign';
import { isEmpty } from '@ember/utils';
import { SafeString } from 'handlebars';

interface DataActionTypeValueEditorArgs {
  campaign: CampaignModel;
  type: DataActionConditionType;
  value: string;
  errors?: (string | SafeString)[];
  onChangeType: (type: DataActionConditionType) => unknown;
  onChangeValue: (value: string) => unknown;
  isLoadingAsyncAutocompleteValues?: boolean;
  autocompleteValues?: Partial<Record<DataActionConditionType, { label: string; value: string; path: string[] }[]>>;
  supportedConditionTypes: DataActionConditionType[];
  updateHighlight: (highlight: Select['highlighted']) => unknown;
  hasReadOnlyPermission: boolean;
}

function convertValueFromXAttributeToLiquidFactory(type: DataActionConditionType) {
  let topLevelKey = getTopLevelKeyFromConditionType(type) as string;

  return function (value: string) {
    if (isEmpty(value)) {
      return '';
    }

    return `{{${topLevelKey}.${value}}}`;
  };
}

function convertValueFromLiquidToXAttributeFactory(type: DataActionConditionType) {
  let topLevelKey = getTopLevelKeyFromConditionType(type) as string;

  return function (value: string) {
    let paths = findUsedVariablePaths(topLevelKey, value);

    if (paths.length === 0) {
      return '';
    }

    let [firstPath] = paths;

    // drop the first item from the path, which would be trigger/event/customer
    firstPath.shift();

    return keyPathToString(firstPath);
  };
}

function convertValueFromXAttributeToJavaScriptFactory(type: DataActionConditionType) {
  let topLevelKey = getTopLevelKeyFromConditionType(type) as string;

  return function (value: string) {
    if (isEmpty(value)) {
      return '';
    }

    return `return ${topLevelKey}.${value};`;
  };
}

function convertValueNoop(value: string) {
  return value;
}

function convertValueClear() {
  return '';
}

const CONVERT_FUNCTION_MAP: Partial<
  Record<DataActionConditionType, Partial<Record<DataActionConditionType, (value: string) => string>>>
> = {
  [DataActionConditionType.Static]: {
    [DataActionConditionType.Static]: convertValueNoop,
  },
  [DataActionConditionType.TriggerAttribute]: {
    [DataActionConditionType.Liquid]: convertValueFromXAttributeToLiquidFactory(
      DataActionConditionType.TriggerAttribute,
    ),
    [DataActionConditionType.JavaScript]: convertValueFromXAttributeToJavaScriptFactory(
      DataActionConditionType.TriggerAttribute,
    ),
    [DataActionConditionType.TriggerAttribute]: convertValueNoop,
  },
  [DataActionConditionType.ProfileAttribute]: {
    [DataActionConditionType.Liquid]: convertValueFromXAttributeToLiquidFactory(
      DataActionConditionType.ProfileAttribute,
    ),
    [DataActionConditionType.JavaScript]: convertValueFromXAttributeToJavaScriptFactory(
      DataActionConditionType.ProfileAttribute,
    ),
    [DataActionConditionType.ProfileAttribute]: convertValueNoop,
  },
  [DataActionConditionType.EventAttribute]: {
    [DataActionConditionType.Liquid]: convertValueFromXAttributeToLiquidFactory(DataActionConditionType.EventAttribute),
    [DataActionConditionType.JavaScript]: convertValueFromXAttributeToJavaScriptFactory(
      DataActionConditionType.EventAttribute,
    ),
    [DataActionConditionType.EventAttribute]: convertValueNoop,
  },
  [DataActionConditionType.Liquid]: {
    [DataActionConditionType.Liquid]: convertValueNoop,
    [DataActionConditionType.TriggerAttribute]: convertValueFromLiquidToXAttributeFactory(
      DataActionConditionType.TriggerAttribute,
    ),
    [DataActionConditionType.ProfileAttribute]: convertValueFromLiquidToXAttributeFactory(
      DataActionConditionType.ProfileAttribute,
    ),
    [DataActionConditionType.EventAttribute]: convertValueFromLiquidToXAttributeFactory(
      DataActionConditionType.EventAttribute,
    ),
  },
  [DataActionConditionType.JavaScript]: {
    [DataActionConditionType.JavaScript]: convertValueNoop,
  },
};

function getValueConversionFunction(sourceType: DataActionConditionType, targetType: DataActionConditionType) {
  let func = CONVERT_FUNCTION_MAP[sourceType]?.[targetType];

  if (func == null) {
    return convertValueClear;
  }

  return func;
}

export default class DataActionTypeValueEditor extends Component<DataActionTypeValueEditorArgs> {
  get selectedTypeHasValue() {
    return isTypeEditable(this.args.type);
  }

  @action
  onChangeType(type: DataActionConditionType) {
    let valueConversionFunction = getValueConversionFunction(this.args.type, type);
    this.args.onChangeType(type);
    this.args.onChangeValue(valueConversionFunction(this.args.value));
  }

  @action
  onChangeValue(value: string) {
    this.args.onChangeValue(value);
  }
}
